import * as React from "react";

import {AnimatePresence} from "framer-motion";
import {ConnectedProps, connect} from "react-redux";

import {getProfile} from "@client/features/orm";
import {
    actions,
    getHasOutstandingRequests,
    getIsLoading,
} from "@client/features/scenes";

import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import {appStyles} from "@client/style";

import {Menu} from "@client/components/Menu";
import {MyFitnessPalScreen} from "@client/components/MyFitnessPal";
import {Navigation} from "@client/components/Navigation";
import {NoOutstandingRequestsIndicator} from "@client/components/NoOutstandingRequestsIndicator";
import {RatingRequest} from "@client/components/RatingRequest";
import {Scene} from "@client/components/Scene";
import {ShareBar} from "@client/components/ShareBar";
import {SimpleScene} from "@client/components/SimpleScene";
import Dialogs from "@client/containers/Dialogs";
import {Calculator} from "@client/scenes/Calculator";
import {CheckIn} from "@client/scenes/CheckIn";
import {ConfirmPayPal} from "@client/scenes/ConfirmPayPal";
import {Contact} from "@client/scenes/Contact";
import {CreateFood} from "@client/scenes/CreateFood";
import {CreateRecipe} from "@client/scenes/CreateRecipe";
import {CreateRecipeFromMeal} from "@client/scenes/CreateRecipeFromMeal";
import {FoodList} from "@client/scenes/FoodList";
import {Home} from "@client/scenes/Home";
import {InviteUser} from "@client/scenes/InviteUser";
import {Journal, Today} from "@client/scenes/Journal";
import {JournalContact} from "@client/scenes/LearningWeek";
import {Login, MagicLinkLogin} from "@client/scenes/Login";
import {Logout} from "@client/scenes/Logout";
import {Maintenance} from "@client/scenes/Maintenance";
import {Manager} from "@client/scenes/Manager";
import {Migrate} from "@client/scenes/Migrate";
import {PasswordReset, SetPassword} from "@client/scenes/PasswordReset";
import {PeptideDetail} from "@client/scenes/PeptideDetail";
import {Peptides} from "@client/scenes/Peptides";
import {PlanOnboarding} from "@client/scenes/PlanOnboarding";
import {Planner} from "@client/scenes/Planner";
import {Profile} from "@client/scenes/Profile";
import {RecipeList} from "@client/scenes/RecipeList";
import {Onboarding, Registration, WelcomeOnboarding} from "@client/scenes/Registration";
import {Reports} from "@client/scenes/Reports";
import {Share, ShareDebug} from "@client/scenes/Share";
import {StoreUpdateRequired} from "@client/scenes/StoreUpdateRequired";
import {Storyboard} from "@client/scenes/Storyboard";
import {Tutorials} from "@client/scenes/Tutorials";
import {UpdateFood} from "@client/scenes/UpdateFood";
import {UpdateJournalEntry} from "@client/scenes/UpdateJournalEntry";
import {UpdateMeal} from "@client/scenes/UpdateMeal";
import {UpdateMeasurements} from "@client/scenes/UpdateMeasurements";
import {UpdateRecipe} from "@client/scenes/UpdateRecipe";
import {UpdateRecipeEntry} from "@client/scenes/UpdateRecipeEntry";
import {WalkingClub, WalkingClubJoin} from "@client/scenes/WalkingClub";
import * as Workouts from "@client/scenes/Workouts";
import {ImperialCaloriesContext} from "@client/site/context";
import {RootState, useAppDispatch} from "@client/store";
import {classNames} from "@reactivated";

export const {Route} = router.mount({
    base: Home,
    store_update_required: StoreUpdateRequired,
    maintenance: Maintenance,
    storyboard: Storyboard,
    tutorials: Tutorials,
    peptides: Peptides,
    peptide_detail: PeptideDetail,
    share: Share,
    share_debug: ShareDebug,
    login: Login,
    plan_onboarding: PlanOnboarding,
    walking_club: WalkingClub,
    walking_club_join: WalkingClubJoin,
    logout: Logout,
    register: Registration,
    reset: PasswordReset,
    magic_link_login: MagicLinkLogin,
    set_password: SetPassword,
    profile: Profile,
    today: Today,
    journal: Journal,
    onboarding: Onboarding,
    goals: WelcomeOnboarding,
    check_in: CheckIn,
    update_measurements: UpdateMeasurements,
    contact: Contact,
    journal_contact: JournalContact,
    update_journal_entry: UpdateJournalEntry,
    update_meal: UpdateMeal,
    create_recipe_from_meal: CreateRecipeFromMeal,
    create_food: CreateFood,
    food_list: FoodList,
    update_food: UpdateFood,
    recipe_list: RecipeList,
    workouts: Workouts.Workouts,
    create_workout: Workouts.CreateWorkout,
    create_workout_program: Workouts.CreateWorkoutProgram,
    update_workout_template: Workouts.UpdateWorkoutTemplate,
    update_exercise_type: Workouts.UpdateExerciseType,
    create_recipe: CreateRecipe,
    update_recipe: UpdateRecipe,
    update_recipe_entry: UpdateRecipeEntry,
    planner: Planner,
    calculator: Calculator,
    reports: Reports,
    manager: Manager,
    invite_user: InviteUser,
    confirm_paypal: ConfirmPayPal,
    migrate: Migrate,
    myfitnesspal: MyFitnessPalScreen,
});

const mapStateToProps = (state: RootState) => ({
    isScanning: state.search.isScanning,
    profile: getProfile(state),
    isMenuOpen: state.scenes.isMenuOpen,
    isLoading: getIsLoading(state),
    hasOutstandingRequests: getHasOutstandingRequests(state),
    isAskingForRating: state.scenes.isAskingForRating,
    share: state.scenes.share,
    platform: state.boot.platform!,
});

const mapDispatchToProps = {
    ...actions,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

interface Props extends ConnectedProps<typeof connector> {
    children?: React.ReactNode;
    pathname?: string;
}

export const App = connector((props: Props) => {
    const [isImperial, setIsImperial] = React.useState<boolean | null>(null);
    const dispatch = useAppDispatch();

    return (
        <ImperialCaloriesContext.Provider value={{isImperial, setIsImperial}}>
            <Dialogs />
            <div className={appStyles.app}>
                <Menu {...props} />

                <AnimatePresence>
                    <Route pathname={props.pathname}>
                        {({
                            view,
                            options,
                            isResolving,
                            parent,
                            locator,
                            tabs,
                            actions,
                        }) => {
                            React.useEffect(() => {
                                dispatch({type: "CLEAR_SHARE"});
                            }, []);

                            React.useEffect(() => {
                                void analytics.track("SCENE_VIEW", {
                                    scene_name: locator.name,
                                    // TODO: this logs reset token, but so does Postmark. insecure.
                                    scene_params: locator.params,
                                });
                            }, []);

                            const back =
                                options.back != null && typeof options.back != "string"
                                    ? options.back
                                    : parent != null && options.back != null
                                      ? {locator: parent, title: options.back}
                                      : null;

                            if (options.scene?.kind == "EMPTY") {
                                return (
                                    <>
                                        <title>{options.title}</title>
                                        {props.hasOutstandingRequests === false && (
                                            <NoOutstandingRequestsIndicator />
                                        )}
                                        {view}
                                    </>
                                );
                            }

                            if (options.scene?.kind == "SIMPLE") {
                                return (
                                    <SimpleScene
                                        view={view}
                                        options={options}
                                        parent={parent}
                                        locator={locator}
                                        tabs={tabs}
                                        actions={actions}
                                        close={null}
                                        isLoading={props.isLoading}
                                        transition={router.transition}
                                    />
                                );
                            }

                            if (options.scene?.kind == "BLANK") {
                                return (
                                    <>
                                        <style>
                                            {`
                                                body {
                                                    background-color: #362848;
                                                }
                                            `}
                                        </style>
                                        <title>{options.title}</title>
                                        {props.hasOutstandingRequests === false && (
                                            <NoOutstandingRequestsIndicator />
                                        )}
                                        {view}
                                    </>
                                );
                            }

                            return (
                                <>
                                    {/* Used by selenium to avoid deadlocks with active XHR requests still pending
                                    while the main Selenium function finishes and the test finishes. Django will then
                                    try to flush tables but can't because Ajax requests are hitting the DB

                                    See: https://github.com/pytest-dev/pytest-django/issues/454
                                */}
                                    {options.dialogs}
                                    {props.hasOutstandingRequests === false && (
                                        <NoOutstandingRequestsIndicator />
                                    )}

                                    <div
                                        className={classNames(
                                            appStyles.navigationAndManager,
                                            props.isScanning
                                                ? "invisible"
                                                : "bg-stone-50",
                                        )}
                                    >
                                        <Navigation
                                            {...props}
                                            isLoading={
                                                (options.scene == null ||
                                                    options.scene.kind == "FULL" ||
                                                    options.scene.kind ==
                                                        "NO_BOTTOM_BORDER") &&
                                                (props.isLoading || isResolving)
                                            }
                                            highlight={false}
                                            title={options.title}
                                            more={options.more}
                                            back={back}
                                        >
                                            {options.titleSection}
                                        </Navigation>
                                    </div>
                                    <AnimatePresence>
                                        {props.isAskingForRating !== false && (
                                            <RatingRequest
                                                {...props}
                                                isAskingForRating={
                                                    props.isAskingForRating
                                                }
                                            />
                                        )}
                                    </AnimatePresence>

                                    {props.share != null && (
                                        <ShareBar share={props.share} />
                                    )}
                                    {options.scene?.kind == "MENU_ONLY" ? (
                                        view
                                    ) : (
                                        <Scene
                                            actions={actions}
                                            actionsStyle={options.actionsStyle}
                                            noBottomBorder={
                                                options.scene?.kind ==
                                                "NO_BOTTOM_BORDER"
                                            }
                                            tabs={tabs.map((tab) =>
                                                tab.icon == false ||
                                                typeof tab.icon != "string"
                                                    ? null
                                                    : {
                                                          ...tab,
                                                          icon: tab.icon,
                                                          isSelected: tab.isActive,
                                                          link: tab.locator,
                                                      },
                                            )}
                                            topSection={options.topSection}
                                            bottomSection={options.bottomSection}
                                            more={options.more}
                                            onRefresh={options.onRefresh}
                                            locator={locator}
                                        >
                                            {view}
                                        </Scene>
                                    )}
                                </>
                            );
                        }}
                    </Route>
                </AnimatePresence>
            </div>
        </ImperialCaloriesContext.Provider>
    );
});
